import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, InputGroup, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommunityCard from '../components/CommunityCard';
import { useCommunities, useDeleteCommunity } from '../hooks/useCommunities';
import { useToast } from '../components/ToastNotification';
import { useAuth } from '../context/AuthContext';
import { theme } from '../theme';

const CommunityListPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { user, isAdmin, isCountryManager, canEdit } = useAuth();
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: communities = [], isLoading, isError, error } = useCommunities();
  const deleteMutation = useDeleteCommunity();

  const handleDelete = async (communityId) => {
    if (!window.confirm(t('community_list.delete_confirm'))) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(communityId);
      addToast(t('community_list.delete_success'), 'success');
    } catch (err) {
      addToast(t('community_list.delete_failure', { error: err.message }), 'danger');
    }
  };

  // Filter communities based on search and status
  const filteredCommunities = communities.filter(community => {
    const meta = community.metadata || {};
    const status = meta.status || 'setup';
    const location = meta.location || '';
    const name = community.name || '';
    const matchesStatus = filterStatus === 'all' || status === filterStatus;
    const matchesSearch =
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (isLoading) {
    return (
      <Container fluid className="py-4 text-center">
        <Spinner animation="border" role="status" style={{ color: theme.colors.primary.main }}>
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
        <p className="mt-3 text-muted">{t('community_list.loading_communities')}</p>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>{t('community_list.load_failed_title')}</Alert.Heading>
          <p>{error?.message || t('community_list.load_failed_default')}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex align-items-center mb-2">
          <h2 className="mb-0">
            <span className="me-2">🏘️</span>
            {t('community_list.heading')}
          </h2>
          {isCountryManager && user?.country_name && (
            <Badge
              bg="light"
              text="dark"
              className="ms-3"
              style={{ fontSize: '0.8rem', fontWeight: 500, border: '1px solid #dee2e6' }}
            >
              {user.country_name}
            </Badge>
          )}
          {isAdmin && (
            <Badge
              bg="light"
              text="dark"
              className="ms-3"
              style={{ fontSize: '0.8rem', fontWeight: 500, border: '1px solid #dee2e6' }}
            >
              {t('community_list.all_countries')}
            </Badge>
          )}
        </div>
        <p className="text-muted">
          {isCountryManager
            ? (user?.country_name
                ? t('community_list.subtitle_country_manager', { country: user.country_name })
                : t('community_list.subtitle_country_manager_fallback'))
            : t('community_list.subtitle_admin')}
        </p>
      </div>

      {/* Filters and Actions */}
      <Row className="mb-4">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              placeholder={t('community_list.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{t('community_list.all_status')}</option>
            <option value="active">{t('community_list.active')}</option>
            <option value="setup">{t('community_list.in_setup')}</option>
            <option value="inactive">{t('community_list.inactive')}</option>
          </Form.Select>
        </Col>
        <Col md={5} className="text-end">
          {canEdit && (
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/communities/new')}
              style={{ backgroundColor: theme.colors.primary.main }}
            >
              <span className="me-2">+</span>
              {t('community_list.add_new')}
            </Button>
          )}
        </Col>
      </Row>

      {/* Community Cards Grid */}
      {filteredCommunities.length > 0 ? (
        <Row className="g-4">
          {filteredCommunities.map(community => (
            <Col key={community.id} md={6} lg={4}>
              <CommunityCard community={community} onDelete={canEdit ? handleDelete : undefined} />
            </Col>
          ))}
        </Row>
      ) : (
        <div className="text-center py-5">
          <div className="mb-3" style={{ fontSize: '3rem' }}>📭</div>
          <h4 className="text-muted">{t('community_list.no_found_title')}</h4>
          <p className="text-muted">
            {searchQuery || filterStatus !== 'all'
              ? t('community_list.no_found_filter_hint')
              : t('community_list.no_found_default_hint')}
          </p>
          {!searchQuery && filterStatus === 'all' && canEdit && (
            <Button
              variant="outline-primary"
              className="mt-3"
              onClick={() => navigate('/communities/new')}
            >
              <span className="me-2">+</span>
              {t('community_list.create_first')}
            </Button>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <Row className="mt-5 pt-4 border-top">
        <Col md={4}>
          <div className="text-center p-3">
            <h3 style={{ color: theme.colors.primary.main }}>{communities.length}</h3>
            <div className="text-muted">{t('community_list.stats_total')}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="text-center p-3">
            <h3 style={{ color: theme.colors.semantic.success }}>
              {communities.filter(c => (c.metadata?.status || 'setup') === 'active').length}
            </h3>
            <div className="text-muted">{t('community_list.stats_active')}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="text-center p-3">
            <h3 style={{ color: theme.colors.semantic.info }}>
              {communities.filter(c => (c.metadata?.status || 'setup') === 'setup').length}
            </h3>
            <div className="text-muted">{t('community_list.stats_in_setup')}</div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default CommunityListPage;
